import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  FormManagementExportFormatId,
  FormManagementWorkspaceData,
} from '@/contracts/form-management'
import type {
  ResponseExportDetailContract,
  ResponseExportDataSource,
  ResponseExportServerDelivery,
  ResponseExportServerFormat,
  ResponseExportServerSafeJobContract,
} from '@/contracts/responses'
import { responseExportDataSourceLabels } from '@/contracts/responses'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import {
  downloadResponseExportJobFile,
  getResponseExportDetails,
  processResponseExportJob,
  queueResponseExportJob,
} from '@/lib/data-client/responses'
import {
  type FormManagementExportFile,
  buildFormManagementExportFile,
  downloadFormManagementExportFile,
} from '@/lib/form-management-export'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  Lock,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type FormManagementExportProps = {
  workspaceData: FormManagementWorkspaceData
  canManageForms: boolean
  onMockExport?: (file: FormManagementExportFile) => void
}

type D1ExportState = {
  data: ResponseExportDetailContract | null
  loading: boolean
  error: string | null
}

type BrowserExportState = {
  status: 'idle' | 'downloading' | 'downloaded' | 'error'
  completedPages: number
  totalPages: number
  fileCount: number
  error: string | null
}

type ServerExportState = {
  status: 'idle' | 'queueing' | 'processing' | 'downloading' | 'downloaded' | 'error'
  job: ResponseExportServerSafeJobContract | null
  fileName: string | null
  error: string | null
}

const initialD1ExportState: D1ExportState = {
  data: null,
  loading: false,
  error: null,
}

const initialBrowserExportState: BrowserExportState = {
  status: 'idle',
  completedPages: 0,
  totalPages: 1,
  fileCount: 0,
  error: null,
}

const initialServerExportState: ServerExportState = {
  status: 'idle',
  job: null,
  fileName: null,
  error: null,
}

const formatIcons = {
  csv: FileSpreadsheet,
  excel: FileSpreadsheet,
  pdf: FileText,
  json: FileJson,
} satisfies Record<FormManagementExportFormatId, typeof FileText>

const fieldGroupLabels = {
  metadata: '基本資料',
  answers: '題目答案',
  hiddenFields: '追蹤欄位',
  customer: '客戶欄位',
} as const

const fieldGroupOrder = ['metadata', 'customer', 'hiddenFields', 'answers'] as const

const fieldGroupSwatchStyles = {
  metadata: 'bg-sky-500',
  customer: 'bg-emerald-500',
  hiddenFields: 'bg-amber-500',
  answers: 'bg-violet-500',
} satisfies Record<keyof typeof fieldGroupLabels, string>

const fieldGroupColumnStyles = {
  metadata: 'border-t-2 border-t-sky-300 bg-sky-50/70',
  customer: 'border-t-2 border-t-emerald-300 bg-emerald-50/70',
  hiddenFields: 'border-t-2 border-t-amber-300 bg-amber-50/70',
  answers: 'border-t-2 border-t-violet-300 bg-violet-50/70',
} satisfies Record<keyof typeof fieldGroupLabels, string>

const fieldGroupCellStyles = {
  metadata: 'bg-sky-50/20',
  customer: 'bg-emerald-50/20',
  hiddenFields: 'bg-amber-50/20',
  answers: 'bg-violet-50/20',
} satisfies Record<keyof typeof fieldGroupLabels, string>

const responseExportDataSources = [
  'filed_customers',
  'all_customers',
  'all_responses',
] satisfies ResponseExportDataSource[]
const d1BrowserLocalPageSize = 100
const serverExportRowLimit = 1000
const exportRowLimitOptions = [100, 500, 1000] as const

export function FormManagementExport({
  workspaceData,
  canManageForms,
  onMockExport,
}: FormManagementExportProps) {
  const [selectedFormat, setSelectedFormat] = useState<FormManagementExportFormatId>('csv')
  const [selectedDataSource, setSelectedDataSource] =
    useState<ResponseExportDataSource>('filed_customers')
  const [exportRowLimit, setExportRowLimit] = useState<(typeof exportRowLimitOptions)[number]>(
    serverExportRowLimit,
  )
  const [filingDateFrom, setFilingDateFrom] = useState('')
  const [filingDateTo, setFilingDateTo] = useState('')
  const [page, setPage] = useState(1)
  const isApiMode = isApiDataClientMode()
  const featureVisibility = useSystemFeatureVisibility()
  const [d1ExportState, setD1ExportState] = useState<D1ExportState>(initialD1ExportState)
  const [browserExportState, setBrowserExportState] =
    useState<BrowserExportState>(initialBrowserExportState)
  const [serverExportState, setServerExportState] =
    useState<ServerExportState>(initialServerExportState)
  const formId = workspaceData.snapshotPreview.formId
  const selectedDataSourceLabel = responseExportDataSourceLabels[selectedDataSource]
  const activeWorkspaceData = useMemo(() => {
    if (!isApiMode || !d1ExportState.data) return workspaceData
    return {
      ...workspaceData,
      sourceMeta: d1ExportState.data.sourceMeta,
      panelStates: {
        ...workspaceData.panelStates,
        export: 'ready' as const,
      },
      exportDetailColumns: d1ExportState.data.columns,
      exportDetailRows: d1ExportState.data.rows,
      exportJobPreview: {
        ...workspaceData.exportJobPreview,
        query: `format=csv&dataSource=${d1ExportState.data.dataSource}&limit=${d1ExportState.data.rowLimit}&page=1&mode=d1-browser-local`,
        estimatedRows: d1ExportState.data.totalRows,
        providerState: 'ready' as const,
      },
    }
  }, [d1ExportState.data, isApiMode, workspaceData])
  const pageSize = activeWorkspaceData.exportDetailPageSize
  const totalRows = activeWorkspaceData.exportDetailRows.length
  const hasRows = totalRows > 0
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))
  const currentPage = Math.min(page, totalPages)
  const selectedFormatMeta =
    activeWorkspaceData.exportFormats.find((format) => format.id === selectedFormat) ??
    activeWorkspaceData.exportFormats[0]
  const selectedFields = activeWorkspaceData.exportFields.filter((field) => field.selected)
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return activeWorkspaceData.exportDetailRows.slice(start, start + pageSize)
  }, [activeWorkspaceData.exportDetailRows, currentPage, pageSize])
  const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const rangeEnd = Math.min(currentPage * pageSize, totalRows)
  const usingD1ExportRows = isApiMode && Boolean(d1ExportState.data)
  const serverExportBusy =
    serverExportState.status === 'queueing' ||
    serverExportState.status === 'processing' ||
    serverExportState.status === 'downloading'
  const browserExportBusy = browserExportState.status === 'downloading'
  const serverExportFormat = getResponseServerExportFormat(selectedFormat)
  const d1BrowserLocalPageCount =
    usingD1ExportRows && d1ExportState.data ? getSafeResponseExportTotalPages(d1ExportState.data) : 1
  const shouldDownloadAllD1Pages = usingD1ExportRows && d1BrowserLocalPageCount > 1
  const exportDataLabel = usingD1ExportRows
    ? 'D1 明細'
    : d1ExportState.loading
      ? 'D1 載入中'
      : d1ExportState.error
        ? 'D1 明細錯誤'
        : isApiMode
          ? 'D1 明細'
          : 'Demo 明細'
  const downloadKindLabel = isApiMode || usingD1ExportRows ? '明細檔' : '模擬檔'
  const browserDownloadActionLabel = `${shouldDownloadAllD1Pages ? '下載全部' : '下載'} ${selectedFormatMeta?.label ?? 'CSV'} ${downloadKindLabel}`
  const exportDisabledReason = browserExportBusy
    ? '正在產生多頁明細檔。'
    : d1ExportState.loading
      ? '正在讀取 D1 填寫明細。'
      : d1ExportState.error
        ? `D1 明細讀取失敗：${d1ExportState.error}`
        : !canManageForms
          ? `只讀檢視可查看明細，下載${downloadKindLabel}需要管理員權限。`
          : !hasRows
            ? '目前沒有符合條件的填寫明細可匯出。'
            : undefined
  const exportDisabledReasonId = exportDisabledReason
    ? 'form-management-export-disabled-reason'
    : undefined
  const serverExportDisabledReason = !isApiMode
    ? undefined
    : serverExportBusy
      ? '伺服器匯出工作正在處理中。'
      : !canManageForms
        ? '伺服器匯出需要管理員權限。'
        : !serverExportFormat
          ? '伺服器匯出第一版只支援 CSV / JSON。'
          : undefined
  const serverExportDisabledReasonId = serverExportDisabledReason
    ? 'form-management-server-export-disabled-reason'
    : undefined
  const serverExportStatusLabel = getServerExportStatusLabel(serverExportState)
  const browserExportStatusLabel = getBrowserExportStatusLabel(browserExportState)
  const showBrowserLocalExport = featureVisibility['forms.exportLocalDownload']
  const showWorkerStreamExport = featureVisibility['forms.exportWorkerStream']
  const showExportFilters = featureVisibility['forms.exportFilters']
  const showExportStatus = featureVisibility['forms.exportStatus']

  useEffect(() => {
    if (!isApiMode) {
      setD1ExportState(initialD1ExportState)
      setBrowserExportState(initialBrowserExportState)
      return
    }

    let cancelled = false
    setD1ExportState((current) => ({ ...current, loading: true, error: null }))

    getResponseExportDetails(formId, {
      format: 'csv',
      dataSource: selectedDataSource,
      limit: d1BrowserLocalPageSize,
      page: 1,
      filingDateFrom: filingDateFrom || undefined,
      filingDateTo: filingDateTo || undefined,
    })
      .then((data) => {
        if (cancelled) return
        setD1ExportState({ data, loading: false, error: null })
        setBrowserExportState(initialBrowserExportState)
        setPage(1)
      })
      .catch((error) => {
        if (cancelled) return
        setD1ExportState({
          data: null,
          loading: false,
          error: error instanceof Error ? error.message : 'unknown_error',
        })
        setBrowserExportState(initialBrowserExportState)
      })

    return () => {
      cancelled = true
    }
  }, [filingDateFrom, filingDateTo, formId, isApiMode, selectedDataSource])

  function changePage(nextPage: number) {
    setPage(Math.min(Math.max(nextPage, 1), totalPages))
  }

  async function handleMockExport() {
    if (
      !canManageForms ||
      !hasRows ||
      d1ExportState.loading ||
      d1ExportState.error ||
      browserExportBusy
    ) {
      return
    }

    if (usingD1ExportRows && d1ExportState.data) {
      await handleD1BrowserLocalExport()
      return
    }

    const file = buildFormManagementExportFile({
      format: selectedFormat,
      formTitle: activeWorkspaceData.snapshotPreview.formTitle,
      fileNameScope: usingD1ExportRows
        ? d1ExportState.data?.dataSourceLabel
        : selectedDataSourceLabel,
      columns: activeWorkspaceData.exportDetailColumns,
      rows: activeWorkspaceData.exportDetailRows,
      source: usingD1ExportRows ? 'd1_response_rows' : 'synthetic_mock',
    })

    deliverBrowserExportFile(file)
  }

  async function handleD1BrowserLocalExport() {
    if (!d1ExportState.data) return
    const format = getResponseBrowserLocalFormat(selectedFormat)
    if (!format) return

    let totalD1Pages = getSafeResponseExportTotalPages(d1ExportState.data)
    const pages: ResponseExportDetailContract[] = []

    setBrowserExportState({
      status: 'downloading',
      completedPages: 0,
      totalPages: totalD1Pages,
      fileCount: 0,
      error: null,
    })

    try {
      const firstPage =
        d1ExportState.data.format === format && d1ExportState.data.page === 1
          ? d1ExportState.data
          : await getResponseExportDetails(formId, {
              format,
              dataSource: selectedDataSource,
              limit: d1BrowserLocalPageSize,
              page: 1,
              filingDateFrom: filingDateFrom || undefined,
              filingDateTo: filingDateTo || undefined,
            })
      totalD1Pages = getSafeResponseExportTotalPages(firstPage)
      pages.push(firstPage)
      setBrowserExportState((current) => ({
        ...current,
        totalPages: totalD1Pages,
      }))

      for (let pageNumber = 2; pageNumber <= totalD1Pages; pageNumber += 1) {
        setBrowserExportState((current) => ({
          ...current,
          completedPages: pageNumber - 1,
        }))
        pages.push(
          await getResponseExportDetails(formId, {
            format,
            dataSource: selectedDataSource,
            limit: d1BrowserLocalPageSize,
            page: pageNumber,
            filingDateFrom: filingDateFrom || undefined,
            filingDateTo: filingDateTo || undefined,
          }),
        )
      }

      pages.forEach((detail) => {
        const file = buildFormManagementExportFile({
          format,
          formTitle: detail.formTitle,
          fileNameScope: detail.dataSourceLabel,
          columns: detail.columns,
          rows: detail.rows,
          source: 'd1_response_rows',
        })
        deliverBrowserExportFile(
          totalD1Pages > 1 ? appendBrowserExportFilePart(file, detail.page, totalD1Pages) : file,
        )
      })

      setBrowserExportState({
        status: 'downloaded',
        completedPages: totalD1Pages,
        totalPages: totalD1Pages,
        fileCount: pages.length,
        error: null,
      })
    } catch (error) {
      setBrowserExportState({
        status: 'error',
        completedPages: pages.length,
        totalPages: totalD1Pages,
        fileCount: 0,
        error: getBrowserExportSafeError(error),
      })
    }
  }

  function deliverBrowserExportFile(file: FormManagementExportFile) {
    if (onMockExport) {
      onMockExport(file)
      return
    }

    downloadFormManagementExportFile(file)
  }

  async function handleServerExport(
    delivery: Extract<ResponseExportServerDelivery, 'r2_signed_url' | 'worker_stream'>,
  ) {
    if (!canManageForms || !isApiMode || !serverExportFormat || serverExportBusy) return

    setServerExportState({
      status: 'queueing',
      job: null,
      fileName: null,
      error: null,
    })

    try {
      const queuedJob = await queueResponseExportJob(formId, {
        format: serverExportFormat,
        delivery,
        dataSource: selectedDataSource,
        limit: exportRowLimit,
        includeSensitiveFields: true,
        filingDateFrom: filingDateFrom || undefined,
        filingDateTo: filingDateTo || undefined,
      })
      setServerExportState({
        status: 'processing',
        job: queuedJob,
        fileName: null,
        error: null,
      })

      const readyJob = await processResponseExportJob(formId, queuedJob.exportJobId)
      setServerExportState({
        status: 'downloading',
        job: readyJob,
        fileName: null,
        error: null,
      })

      const file = await downloadResponseExportJobFile(formId, readyJob.exportJobId)
      if (onMockExport) {
        onMockExport(file)
      } else {
        downloadFormManagementExportFile(file)
      }
      setServerExportState({
        status: 'downloaded',
        job: readyJob,
        fileName: file.fileName,
        error: null,
      })
    } catch (error) {
      setServerExportState({
        status: 'error',
        job: null,
        fileName: null,
        error: getServerExportSafeError(error),
      })
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>{exportDataLabel}</Badge>
        <Badge variant="outline">response detail export preview</Badge>
        <Badge variant={activeWorkspaceData.panelStates.export === 'ready' ? 'success' : 'warning'}>
          {activeWorkspaceData.panelStates.export}
        </Badge>
        {isApiMode && serverExportState.status === 'downloaded' && (
          <Badge variant="success">伺服器匯出已下載</Badge>
        )}
        {isApiMode && browserExportState.status === 'downloading' && (
          <Badge variant="warning">{browserExportStatusLabel}</Badge>
        )}
        {isApiMode && browserExportState.status === 'downloaded' && (
          <Badge variant="success">{browserExportStatusLabel}</Badge>
        )}
        {isApiMode && browserExportState.status === 'error' && (
          <Badge variant="destructive">明細下載失敗</Badge>
        )}
        {isApiMode && serverExportBusy && <Badge variant="warning">{serverExportStatusLabel}</Badge>}
        {isApiMode && serverExportState.status === 'error' && (
          <Badge variant="destructive">伺服器匯出失敗</Badge>
        )}
        {!canManageForms && (
          <Badge variant="secondary" className="gap-1">
            <Lock className="size-3" />
            匯出需要管理員權限
          </Badge>
        )}
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h3 className="font-semibold text-slate-950">明細匯出</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="grid gap-2">
                  <label
                    className="text-sm font-medium text-slate-700"
                    htmlFor="response-export-data-source"
                  >
                    資料來源
                  </label>
                  <Select
                    value={selectedDataSource}
                    onValueChange={(value) => {
                      setSelectedDataSource(value as ResponseExportDataSource)
                      setServerExportState(initialServerExportState)
                      setBrowserExportState(initialBrowserExportState)
                      setPage(1)
                    }}
                  >
                    <SelectTrigger id="response-export-data-source" className="bg-white">
                      <SelectValue placeholder="選擇資料來源" />
                    </SelectTrigger>
                    <SelectContent>
                      {responseExportDataSources.map((dataSource) => (
                        <SelectItem key={dataSource} value={dataSource}>
                          {responseExportDataSourceLabels[dataSource]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label
                    className="text-sm font-medium text-slate-700"
                    htmlFor="response-export-row-limit"
                  >
                    匯出筆數
                  </label>
                  <Select
                    value={String(exportRowLimit)}
                    onValueChange={(value) => {
                      setExportRowLimit(Number(value) as (typeof exportRowLimitOptions)[number])
                      setServerExportState(initialServerExportState)
                      setBrowserExportState(initialBrowserExportState)
                    }}
                  >
                    <SelectTrigger id="response-export-row-limit" className="bg-white">
                      <SelectValue placeholder="選擇筆數" />
                    </SelectTrigger>
                    <SelectContent>
                      {exportRowLimitOptions.map((limit) => (
                        <SelectItem key={limit} value={String(limit)}>
                          {limit} 筆
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label
                    className="text-sm font-medium text-slate-700"
                    htmlFor="response-export-filing-date-from"
                  >
                    建檔期間開始
                  </label>
                  <Input
                    id="response-export-filing-date-from"
                    type="date"
                    value={filingDateFrom}
                    max={filingDateTo || undefined}
                    onChange={(event) => {
                      setFilingDateFrom(event.currentTarget.value)
                      setServerExportState(initialServerExportState)
                      setBrowserExportState(initialBrowserExportState)
                      setPage(1)
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <label
                    className="text-sm font-medium text-slate-700"
                    htmlFor="response-export-filing-date-to"
                  >
                    建檔期間結束
                  </label>
                  <Input
                    id="response-export-filing-date-to"
                    type="date"
                    value={filingDateTo}
                    min={filingDateFrom || undefined}
                    onChange={(event) => {
                      setFilingDateTo(event.currentTarget.value)
                      setServerExportState(initialServerExportState)
                      setBrowserExportState(initialBrowserExportState)
                      setPage(1)
                    }}
                  />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <Badge variant="secondary">只匯出已送出資料</Badge>
                <Badge variant="secondary">CSV / JSON</Badge>
                <Badge variant="secondary">含敏感欄位</Badge>
              </div>
            </div>
            <div className="flex flex-col items-start gap-2 xl:items-end">
              {showBrowserLocalExport ? (
                <Button
                  type="button"
                  disabled={
                    !canManageForms ||
                    !hasRows ||
                    d1ExportState.loading ||
                    Boolean(d1ExportState.error) ||
                    browserExportBusy
                  }
                  aria-describedby={exportDisabledReasonId}
                  onClick={handleMockExport}
                >
                  <Download className="size-4" />
                  {browserDownloadActionLabel}
                </Button>
              ) : null}
              {isApiMode && (
                <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                  <Button
                    type="button"
                    disabled={Boolean(serverExportDisabledReason)}
                    aria-describedby={serverExportDisabledReasonId}
                    onClick={() => handleServerExport('r2_signed_url')}
                  >
                    <Download className="size-4" />
                    匯出
                  </Button>
                  {showWorkerStreamExport ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={Boolean(serverExportDisabledReason)}
                      aria-describedby={serverExportDisabledReasonId}
                      onClick={() => handleServerExport('worker_stream')}
                    >
                      <FileText className="size-4" />
                      建立 Worker 串流匯出
                    </Button>
                  ) : null}
                </div>
              )}
              {showBrowserLocalExport && exportDisabledReason && (
                <p
                  id={exportDisabledReasonId}
                  className="max-w-xs text-sm leading-6 text-slate-500 xl:text-right"
                >
                  {exportDisabledReason}
                </p>
              )}
              {isApiMode && serverExportDisabledReason && (
                <p
                  id={serverExportDisabledReasonId}
                  className="max-w-xs text-sm leading-6 text-slate-500 xl:text-right"
                >
                  {serverExportDisabledReason}
                </p>
              )}
              {isApiMode && serverExportState.error && (
                <p className="max-w-xs text-sm leading-6 text-rose-600 xl:text-right">
                  {serverExportState.error}
                </p>
              )}
              {isApiMode && browserExportState.error && (
                <p className="max-w-xs text-sm leading-6 text-rose-600 xl:text-right">
                  {browserExportState.error}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {activeWorkspaceData.exportFormats.map((format) => {
              const Icon = formatIcons[format.id]
              const active = selectedFormat === format.id

              return (
                <button
                  key={format.id}
                  type="button"
                  aria-label={`選擇 ${format.label} 格式`}
                  className={cn(
                    'rounded-md border p-4 text-left transition-colors',
                    active
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-950 ring-1 ring-emerald-200'
                      : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50',
                  )}
                  onClick={() => {
                    setSelectedFormat(format.id)
                    setPage(1)
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          'rounded-md p-2',
                          active ? 'bg-white text-emerald-700' : 'bg-indigo-50 text-indigo-700',
                        )}
                      >
                        <Icon className="size-5" />
                      </span>
                      <div>
                        <div className="font-semibold">{format.label}</div>
                        <div className="mt-1 text-xs text-slate-500">{format.description}</div>
                      </div>
                    </div>
                    <Badge variant={active ? 'success' : 'secondary'}>
                      {active ? '已選' : '可選'}
                    </Badge>
                  </div>
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-col gap-3 border-b border-slate-200 p-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="font-semibold text-slate-950">填寫明細</h3>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                每頁 5 筆，表格可橫向捲動檢視所有答案欄位。
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="font-medium text-slate-700">欄位群組</span>
                {fieldGroupOrder.map((group) => (
                  <span
                    key={group}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1"
                  >
                    <span
                      className={cn('size-2 rounded-full', fieldGroupSwatchStyles[group])}
                      aria-hidden="true"
                    />
                    {fieldGroupLabels[group]}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Badge variant="secondary">{selectedFormatMeta?.label ?? 'CSV'}</Badge>
              <Badge variant="outline">{totalRows} 筆</Badge>
            </div>
          </div>

          <div className="p-5 pt-0">
            {hasRows ? (
              <>
                <p className="mb-3 text-xs text-slate-500">手機或窄版畫面可左右滑動表格。</p>
                <div className="rounded-md border border-slate-200">
                  <Table aria-label="填寫明細匯出資料" className="min-w-[1680px]">
                    <TableHeader>
                      <TableRow>
                        {activeWorkspaceData.exportDetailColumns.map((column) => (
                          <TableHead
                            key={column.id}
                            className={cn(
                              'whitespace-nowrap text-xs',
                              fieldGroupColumnStyles[column.group],
                              column.group === 'answers' && 'min-w-48',
                            )}
                          >
                            <div>{column.label}</div>
                            <div
                              className="mt-1 text-[11px] font-normal text-slate-500"
                              aria-hidden="true"
                            >
                              {fieldGroupLabels[column.group]}
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageRows.map((row) => (
                        <TableRow key={row.id} data-testid={`export-detail-row-${row.recordNo}`}>
                          {activeWorkspaceData.exportDetailColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              className={cn(
                                'max-w-60 align-top text-xs leading-5 text-slate-700',
                                fieldGroupCellStyles[column.group],
                                column.id === 'recordNo' && 'font-semibold text-slate-950',
                                column.group === 'answers' && 'min-w-48',
                              )}
                            >
                              <div className="line-clamp-3 break-words">
                                {row.cells[column.id] ?? '-'}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-500">
                    {`顯示 ${rangeStart}-${rangeEnd} / ${totalRows} 筆`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => changePage(currentPage - 1)}
                    >
                      <ChevronLeft className="size-4" />
                      上一頁
                    </Button>
                    <span className="rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-700">
                      {`第 ${currentPage} / ${totalPages} 頁`}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => changePage(currentPage + 1)}
                    >
                      下一頁
                      <ChevronRight className="size-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <output className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                <div className="font-medium text-slate-800">目前沒有符合條件的填寫明細</div>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {isApiMode
                    ? d1ExportState.error
                      ? 'D1 明細讀取失敗，修正 API 或 D1 schema 後會在這裡列出可匯出的明細。'
                      : '目前 D1 沒有符合條件的已送出填寫明細。'
                    : '目前未顯示 mock 填寫記錄；等正式填寫記錄接上後會在這裡列出可匯出的明細。'}
                </p>
              </output>
            )}
          </div>
        </CardContent>
      </Card>

      {showExportFilters || showExportStatus ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          {showExportFilters ? (
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-slate-950">欄位與篩選</h3>
                  <Badge variant="secondary">{selectedFields.length} 個欄位預選</Badge>
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    {activeWorkspaceData.exportFields.map((field) => (
                      <div
                        key={field.id}
                        className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2"
                      >
                        <div>
                          <div className="text-sm font-medium text-slate-800">{field.label}</div>
                          <div className="text-xs text-slate-500">
                            {fieldGroupLabels[field.group]}
                          </div>
                        </div>
                        <Badge variant={field.selected ? 'success' : 'secondary'}>
                          {field.selected ? '已選' : '未選'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {activeWorkspaceData.exportFilters.map((filter) => (
                      <div
                        key={filter.id}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2"
                      >
                        <div className="text-xs text-slate-500">{filter.label}</div>
                        <div className="mt-1 text-sm font-medium text-slate-900">
                          {filter.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {showExportStatus ? (
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-950">匯出狀態</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  顯示目前明細匯出 route、權限、provider 與 job 狀態。
                </p>
                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <div className="rounded-md bg-slate-50 px-3 py-2">
                    Route: {activeWorkspaceData.exportJobPreview.route}
                  </div>
                  <div className="rounded-md bg-slate-50 px-3 py-2">
                    權限: {activeWorkspaceData.exportJobPreview.permissionRequired}
                  </div>
                  <div className="rounded-md bg-slate-50 px-3 py-2">
                    Provider: {activeWorkspaceData.exportJobPreview.providerState}
                  </div>
                  {isApiMode && (
                    <>
                      <div className="rounded-md bg-slate-50 px-3 py-2">
                        Server job: {serverExportStatusLabel}
                      </div>
                      <div className="rounded-md bg-slate-50 px-3 py-2">
                        Browser-local: {browserExportStatusLabel}
                      </div>
                      {serverExportState.job && (
                        <div className="rounded-md bg-slate-50 px-3 py-2">
                          伺服器: {serverExportState.job.downloadMode} /{' '}
                          {serverExportState.job.status}
                        </div>
                      )}
                      {serverExportState.fileName && (
                        <div className="rounded-md bg-slate-50 px-3 py-2">
                          檔案: {serverExportState.fileName}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function getResponseServerExportFormat(
  format: FormManagementExportFormatId,
): ResponseExportServerFormat | null {
  if (format === 'csv' || format === 'json') return format
  return null
}

function getResponseBrowserLocalFormat(
  format: FormManagementExportFormatId,
): ResponseExportDetailContract['format'] | null {
  if (format === 'csv' || format === 'json') return format
  return null
}

function getSafeResponseExportTotalPages(detail: ResponseExportDetailContract) {
  if (!Number.isFinite(detail.totalPages)) return 1
  return Math.max(1, Math.round(detail.totalPages))
}

function appendBrowserExportFilePart(
  file: FormManagementExportFile,
  page: number,
  totalPages: number,
): FormManagementExportFile {
  const extensionIndex = file.fileName.lastIndexOf('.')
  const baseName = extensionIndex > 0 ? file.fileName.slice(0, extensionIndex) : file.fileName
  const extension = extensionIndex > 0 ? file.fileName.slice(extensionIndex) : ''

  return {
    ...file,
    fileName: `${baseName}-part-${page}-of-${totalPages}${extension}`,
  }
}

function getBrowserExportStatusLabel(state: BrowserExportState) {
  if (state.status === 'downloading') {
    return `明細下載 ${state.completedPages} / ${state.totalPages}`
  }
  if (state.status === 'downloaded') {
    return state.fileCount > 1 ? `多頁下載已完成：${state.fileCount} 個檔案` : '明細下載已完成'
  }
  if (state.status === 'error') return '明細下載失敗'
  return '尚未下載明細檔'
}

function getBrowserExportSafeError(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as { code?: unknown }).code ?? 'unknown_error')
    return `明細檔下載暫時無法完成，安全代碼 ${code}。`
  }
  return '明細檔下載暫時無法完成，請稍後再試。'
}

function getServerExportStatusLabel(state: ServerExportState) {
  if (state.status === 'queueing') return '伺服器匯出排程中'
  if (state.status === 'processing') return '伺服器匯出處理中'
  if (state.status === 'downloading') return '伺服器匯出下載中'
  if (state.status === 'downloaded') return '伺服器匯出已下載'
  if (state.status === 'error') return '伺服器匯出失敗'
  return '尚未建立伺服器匯出'
}

function getServerExportSafeError(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as { code?: unknown }).code ?? 'unknown_error')
    return `伺服器匯出暫時無法完成，安全代碼 ${code}。`
  }
  return '伺服器匯出暫時無法完成，請稍後再試。'
}
